import { Globe, Mail, Mountain } from 'lucide-react'

interface FooterProps {
  currentSite: {
    name: string
    description: string
    domain: string
  }
  relatedSites: Array<{
    name: string
    description: string
    domain: string
  }>
}

export default function Footer({ currentSite, relatedSites }: FooterProps) {
  return (
    <footer className="bg-glacier-900 text-white section-padding">
      <div className="container-padding mx-auto max-w-7xl">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
          {/* Current Site Info */}
          <div>
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-10 h-10 bg-gradient-to-br from-glacier-400 to-glacier-600 rounded-lg flex items-center justify-center">
                <Mountain className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold">{currentSite.name}</h3>
                <p className="text-glacier-300 text-sm">{currentSite.description}</p>
              </div>
            </div>
            <p className="text-glacier-300 mb-6">
              Dedicated to glacier conservation and awareness. Join us in protecting our planet's frozen treasures.
            </p>
            <div className="flex items-center space-x-4">
              <a
                href="mailto:info@worlddayforglaciers.com"
                className="flex items-center text-glacier-300 hover:text-white transition-colors"
              >
                <Mail className="w-5 h-5 mr-2" />
                info@worlddayforglaciers.com
              </a>
            </div>
          </div>

          {/* Related Sites */}
          <div>
            <h3 className="text-xl font-bold mb-6">Related Glacier Sites</h3>
            <div className="space-y-6">
              {relatedSites.map((site) => (
                <a
                  key={site.domain}
                  href={`https://${site.domain}`}
                  className="block p-4 bg-glacier-800/50 rounded-lg hover:bg-glacier-800 transition-colors group"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <div className="flex items-start space-x-3">
                    <Globe className="w-5 h-5 text-glacier-400 mt-1" />
                    <div>
                      <h4 className="font-semibold group-hover:text-glacier-300 transition-colors">
                        {site.name}
                      </h4>
                      <p className="text-glacier-400 text-sm mt-1">
                        {site.description}
                      </p>
                      <p className="text-glacier-500 text-xs mt-2">
                        {site.domain}
                      </p>
                    </div>
                  </div>
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-xl font-bold mb-6">Quick Links</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <h4 className="font-semibold text-glacier-300 mb-3">Explore</h4>
                <ul className="space-y-2">
                  <li><a href="/glaciers" className="text-glacier-400 hover:text-white transition-colors">Glaciers</a></li>
                  <li><a href="/data" className="text-glacier-400 hover:text-white transition-colors">Data</a></li>
                  <li><a href="/research" className="text-glacier-400 hover:text-white transition-colors">Research</a></li>
                  <li><a href="/maps" className="text-glacier-400 hover:text-white transition-colors">Maps</a></li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-glacier-300 mb-3">Get Involved</h4>
                <ul className="space-y-2">
                  <li><a href="/volunteer" className="text-glacier-400 hover:text-white transition-colors">Volunteer</a></li>
                  <li><a href="/donate" className="text-glacier-400 hover:text-white transition-colors">Donate</a></li>
                  <li><a href="/events" className="text-glacier-400 hover:text-white transition-colors">Events</a></li>
                  <li><a href="/education" className="text-glacier-400 hover:text-white transition-colors">Education</a></li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-glacier-800 mt-12 pt-8 text-center">
          <p className="text-glacier-400">
            © {new Date().getFullYear()} {currentSite.name}. All rights reserved. 
            Part of the global glacier conservation network.
          </p>
          <p className="text-glacier-500 text-sm mt-2">
            Connecting glacier enthusiasts worldwide through {relatedSites.length + 1} specialized platforms.
          </p>
        </div>
      </div>
    </footer>
  )
}